<template>
    <div>
        <DataTable
            :columns="columns"
            :items="items"
            :perPage="itemsPerPage"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            @page-changed="changePage"
            @per-page-changed="changePerPage"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportCategories"
                        />
                    </div>

                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('food.carte.add') }}</span>
                    </BaseButton>
                </div>
            </template>
            <template #column-name="{ item }">
                <span class="poppinsMedium">{{ item.name }}</span>
            </template>
            <template #column-tables_count="{ item }">
                {{ item.tables_count }}
            </template>
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEdit(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        :title="$t('common.delete')"
                        @click="askDelete(item)"
                        class="ppv2-p-1 ppv2-text-red-500 hover:ppv2-bg-red-500 hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular ppv2-text-black">
                    {{ $tc('food.table_categories.selected', selectedCount, { count: selectedCount }) }}
                </span>
                <BaseButton
                    variant="dangerOutline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="18" />
                    <span>{{ $t('common.delete') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <CustomDialog
            v-model="showForm"
            :defaultTitle="editing ? $t('food.table_categories.edit_title') : $t('food.table_categories.add_title')"
        >
            <template #content>
                <BaseInput
                    v-model="name"
                    :label="$t('food.table_categories.name_label')"
                    :error="formErrors.name ? formErrors.name[0] : ''"
                    :state="formErrors.name ? 'error' : 'default'"
                    required
                    @input="clearNameError"
                />
            </template>
            <template #actions>
                <BaseButton variant="primary" :disabled="!name || isSaving" @click="save">{{
                    $t('food.table_categories.save')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <CustomDialog v-model="showDelete" :defaultTitle="$t('food.table_categories.delete_title')">
            <template #content>
                <p class="ppv2-text-center">{{ deleteDescription }}</p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showDelete = false">{{
                    $t('food.table_categories.delete_cancel')
                }}</BaseButton>
                <BaseButton variant="danger" @click="confirmDelete">{{ $t('food.table_categories.delete_confirm') }}</BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import DataTable from '../../Shared/DataTable.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseInput from '../../Shared/BaseInput.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import Search from '../Experience/components/Search.vue'
import { getPerPage, setPerPage } from '../../../utils/perPage'
import { Trash2, SquarePen, Plus } from 'lucide-vue'

export default {
    name: 'FoodTableCategories',
    components: { DataTable, BaseButton, BaseInput, CustomDialog, ExportSelect, Search, Trash2, SquarePen, Plus },
    data() {
        return {
            items: [],
            currentPage: 1,
            itemsPerPage: getPerPage('food_table_categories_items_per_page', 10),
            totalPages: 1,
            showForm: false,
            showDelete: false,
            editing: null,
            deleting: null,
            selectedItems: [],
            deleteAction: null,
            itemToDelete: null,
            sortBy: 'name',
            sortOrder: 'asc',
            name: '',
            formErrors: {},
            isSaving: false,
            searchQuery: '',
            isExporting: false,
        }
    },
    computed: {
        columns() {
            return [
                { key: 'name', label: this.$t('food.table_categories.columns.category'), sortable: true, type: 'text' },
                { key: 'tables_count', label: this.$t('food.table_categories.columns.tables'), sortable: true, type: 'number' },
                { key: 'actions', label: this.$t('food.table_categories.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        deleteDescription() {
            if (this.deleteAction === 'multiple') {
                return this.$t('food.table_categories.confirm_delete_multiple', { count: this.selectedItems.length })
            }

            return this.deleting && this.deleting.tables_count > 0
                ? this.$t('food.table_categories.linked_tables', { count: this.deleting.tables_count })
                : this.$t('food.table_categories.confirm_delete')
        },
    },
    methods: {
        changePage(page) {
            this.currentPage = page
            this.fetchData()
        },
        changePerPage(value) {
            this.itemsPerPage = Number(value)
            setPerPage('food_table_categories_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(value) {
            this.searchQuery = value
            this.currentPage = 1
            this.fetchData()
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        openCreate() {
            this.editing = null
            this.name = ''
            this.formErrors = {}
            this.showForm = true
        },
        openEdit(item) {
            this.editing = item
            this.name = item.name
            this.formErrors = {}
            this.showForm = true
        },
        askDelete(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item.id
            this.deleting = item
            this.showDelete = true
        },
        async save() {
            this.isSaving = true
            try {
                const isEditing = Boolean(this.editing)
                const url = this.editing
                    ? `/back-office/v3/food/table-categories/update/${this.editing.id}`
                    : '/back-office/v3/food/table-categories/store'
                await axios.post(url, { name: this.name })
                this.showForm = false
                await this.fetchData()
                this.$emit('refresh-stats')
                this.$toast?.success(
                    this.$t(isEditing ? 'food.table_categories.updated_success' : 'food.table_categories.added_success')
                )
            } catch (error) {
                this.formErrors = error.response?.data?.errors || {}
                const message =
                    this.formErrors.name?.[0] || error.response?.data?.message || this.$t('food.table_categories.save_error')
                this.$toast?.error(message)
            } finally {
                this.isSaving = false
            }
        },
        clearNameError() {
            if (this.formErrors.name) this.$delete(this.formErrors, 'name')
        },
        toggleItemSelection(id) {
            const index = this.selectedItems.indexOf(id)
            index > -1 ? this.selectedItems.splice(index, 1) : this.selectedItems.push(id)
        },
        toggleSelectAll() {
            this.selectedItems = this.selectedItems.length === this.items.length ? [] : this.items.map((item) => item.id)
        },
        deleteSelectedItems() {
            if (!this.selectedItems.length) return

            this.deleteAction = 'multiple'
            this.showDelete = true
        },
        async confirmDelete() {
            const ids = this.deleteAction === 'single' ? [this.itemToDelete] : this.selectedItems

            try {
                await axios.delete('/back-office/v3/food/table-categories/delete', { data: { ids } })
                this.showDelete = false
                this.selectedItems = []
                this.deleting = null
                this.deleteAction = null
                this.itemToDelete = null
                await this.fetchData()
                this.$emit('refresh-stats')
                this.$toast?.success(this.$t('food.table_categories.deleted_success'))
            } catch (error) {
                this.$toast?.error(error.response?.data?.message || this.$t('food.table_categories.delete_error'))
            }
        },
        async fetchData() {
            const response = await axios.get('/back-office/v3/food/table-categories/list', {
                params: {
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    search: this.searchQuery,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                },
            })
            this.items = response.data.data || []
            this.totalPages = response.data.last_page || 1
        },
        async exportCategories(format = 'csv') {
            if (this.isExporting) return

            this.isExporting = true
            try {
                const response = await axios.get('/back-office/v3/food/table-categories/export', {
                    params: { format, search: this.searchQuery },
                    responseType: 'blob',
                })
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                const extension = format === 'excel' ? 'xlsx' : 'csv'
                link.href = url
                link.setAttribute('download', `table_categories_export_${format}.${extension}`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.$toast?.error(error.response?.data?.message || this.$t('food.table_categories.export_error'))
            } finally {
                this.isExporting = false
            }
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>
